import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { useState, useEffect } from 'react';
import { updateData } from '../../../services';
import Modal from '../../helpers/Modal';
export const EditCompanyForm = ({ company, onCompanyUpdated }) => {
    const { token, base_url, setCompany } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [formData, setFormData] = useState({
        company_name: '',
        company_type: '',
        street_address_1: '',
        street_address_2: '',
        city: '',
        state: '',
        zip_code: '',
        primary_contact_first_name: '',
        primary_contact_last_name: '',
        primary_contact_email: '',
        primary_contact_phone: '',
    });

    const companyTypes = [
        'End User / Property Owner',
        'Installer / Solar Pro',
        'Electrical / General Contractor',
        'Distributor',
        'EPC / Developer'
    ];

    // Initialize form data with company values when component mounts or company changes
    useEffect(() => {
        if (company) {
            setFormData({
                company_name: company.company_name || '',
                company_type: company.company_type || '',
                discount_rate: company.discount_rate || '',
                street_address_1: company.street_address_1 || '',
                street_address_2: company.street_address_2 || '',
                city: company.city || '',
                state: company.state || '',
                zip_code: company.zip_code || '',
                primary_contact_first_name: company.primary_contact_first_name || '',
                primary_contact_last_name: company.primary_contact_last_name || '',
                primary_contact_email: company.primary_contact_email || '',
                primary_contact_phone: company.primary_contact_phone || '',
                sales_tax_id: company.sales_tax_id || '',
                sales_tax_forms_received: company.sales_tax_forms_received || false
            });
        }
    }, [company]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = {
            company_name: formData.company_name,
            company_type: formData.company_type,
            street_address_1: formData.street_address_1,
            street_address_2: formData.street_address_2,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            primary_contact_first_name: formData.primary_contact_first_name,
            primary_contact_last_name: formData.primary_contact_last_name,
            primary_contact_email: formData.primary_contact_email,
            primary_contact_phone: formData.primary_contact_phone,
        };

        try {
            console.log('Updating company:', company.id);
            const endpoint = `${base_url}/apis/admin/companies/${company.id}`;
            const updated_company = await updateData(token, endpoint, data);

            if (updated_company.company) {
                // Update the company in context
                setCompany(updated_company.company);

                // Call the callback if provided
                if (onCompanyUpdated) {
                    onCompanyUpdated(updated_company.company);
                }

                // Show success alert
                window.showAlert(200, 'Company updated successfully!');
                console.log('Company updated successfully!');
                
                // Close modal and navigate
                setIsModalOpen(false);
            }
        } catch (error) {
            console.error('Error updating company:', error);
            
            // Show error alert based on error type
            if (error.status === 404) {
                window.showAlert(404, 'Company not found');
            } else if (error.status >= 400 && error.status < 500) {
                window.showAlert(error.status, 'Invalid company data. Please check your input.');
            } else {
                window.showAlert(500, 'Failed to update company. Please try again.');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (!company) {
        return <div>No company data available</div>;
    }

    const editCompanyForm = (
        <div>
            <form onSubmit={handleSubmit} className="company-form-grid">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="company_name" className="form-label">
                            Company Name *
                        </label>
                        <input
                            type="text"
                            id="company_name"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter company name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="company_type" className="form-label">
                            Company Type
                        </label>
                        <select
                            id="company_type"
                            name="company_type"
                            value={formData.company_type}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="">Select company type</option>
                            {companyTypes.map((type, index) => (
                                <option key={index} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="street_address_1" className="form-label">
                            Street Address 1
                        </label>
                        <input
                            type="text"
                            id="street_address_1"
                            name="street_address_1"
                            value={formData.street_address_1}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter primary address"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="street_address_2" className="form-label">
                            Street Address 2
                        </label>
                        <input
                            type="text"
                            id="street_address_2"
                            name="street_address_2"
                            value={formData.street_address_2}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter secondary address (optional)"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="city" className="form-label">
                            City
                        </label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter city"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="state" className="form-label">
                            State
                        </label>
                        <input
                            type="text"
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter state"
                            maxLength="2"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="zip_code" className="form-label">
                            ZIP Code
                        </label>
                        <input
                            type="text"
                            id="zip_code"
                            name="zip_code"
                            value={formData.zip_code}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter ZIP code"
                            maxLength="5"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="primary_contact_first_name" className="form-label">
                            Primary Contact First Name
                        </label>
                        <input
                            type="text"
                            id="primary_contact_first_name"
                            name="primary_contact_first_name"
                            value={formData.primary_contact_first_name}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter first name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="primary_contact_last_name" className="form-label">
                            Primary Contact Last Name
                        </label>
                        <input
                            type="text"
                            id="primary_contact_last_name"
                            name="primary_contact_last_name"
                            value={formData.primary_contact_last_name}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter last name"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="primary_contact_email" className="form-label">
                            Primary Contact Email
                        </label>
                        <input
                            type="email"
                            id="primary_contact_email"
                            name="primary_contact_email"
                            value={formData.primary_contact_email}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter email address"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="primary_contact_phone" className="form-label">
                            Primary Contact Phone
                        </label>
                        <input
                            type="tel"
                            id="primary_contact_phone"
                            name="primary_contact_phone"
                            value={formData.primary_contact_phone}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={() => setIsModalOpen(false)}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                    >
                        Update Company
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div>
            <button className="btn btn-secondary" onClick={() => setIsModalOpen(true)}>
                <FontAwesomeIcon icon={faPen} />
            </button>
            {isModalOpen && (
                <Modal form={editCompanyForm} setIsModalOpen={setIsModalOpen} isWide={true} />
            )}
        </div>
    )
};
